// Content for the Communications page "La solution" section (ATNA orchestration).
import { WATER_MAIN_PHOTO } from './communicationsHero'

export type Bi = { fr: string; en: string }

export const FAMILY_PHOTO = 'https://cdn.magicpatterns.com/patterns/generated-images/db9f5230-0d67-41d4-9eb6-101b1e28a6cd.jpg'
export const OPERATOR_AVATAR = 'https://cdn.magicpatterns.com/patterns/generated-images/d5a1e198-c322-43ad-a39b-efb759aeff53.jpg'
export { WATER_MAIN_PHOTO }

export const solutionIntro = {
  eyebrow: { fr: 'La solution', en: 'The solution' },
  title: { fr: 'Un seul message. Une seule intelligence.', en: 'One communication. One intelligence.' },
  accent: { fr: 'Tous les bons canaux.', en: 'All the right channels.' },
  body: {
    fr: [
      'Communications ATNA relie l’identité, le contexte et les systèmes de l’organisation pour déterminer qui joindre,',
      'préparer le bon message, orchestrer sa diffusion et suivre ce qui s’est réellement passé.',
    ],
    en: [
      'ATNA Communications connects identity, context and the organization’s systems to determine who to reach,',
      'prepare the right message, orchestrate delivery and track what actually happened.',
    ],
  },
}

export const solutionInputs: { key: 'identity' | 'context' | 'event' | 'data'; title: Bi; body: Bi }[] = [
  { key: 'identity', title: { fr: 'Identité', en: 'Identity' }, body: { fr: 'Citoyens, familles, propriétés, commerces', en: 'Residents, families, properties, businesses' } },
  { key: 'context', title: { fr: 'Contexte', en: 'Context' }, body: { fr: 'Localisation, secteur, services, événements', en: 'Location, sector, services, events' } },
  { key: 'event', title: { fr: 'Événement', en: 'Event' }, body: { fr: 'Urgence, travaux, fermeture, changement', en: 'Emergency, roadwork, closure, change' } },
  { key: 'data', title: { fr: 'Données', en: 'Data' }, body: { fr: 'Systèmes municipaux et partenaires', en: 'Municipal and partner systems' } },
]

export const atnaCard = {
  label: { fr: 'Communications ATNA', en: 'ATNA Communications' },
  verbs: { fr: 'Comprend · Prépare · Vérifie · Orchestre', en: 'Understands · Prepares · Verifies · Orchestrates' },
  command: {
    fr: 'Bris d’aqueduc sur Grande-Allée jusqu’à 16 h. Avise les personnes concernées.',
    en: 'Water main break on Grande-Allée until 4 PM. Notify the people affected.',
  },
  rows: [
    { key: 'audience', title: { fr: 'Public cible déterminé', en: 'Audience identified' }, sub: { fr: '1 842 personnes concernées', en: '1,842 people affected' }, warn: false },
    { key: 'notice', title: { fr: 'Avis préparé', en: 'Notice prepared' }, sub: { fr: 'FR + EN · Modèle officiel', en: 'FR + EN · Official template' }, warn: false },
    { key: 'channels', title: { fr: 'Canaux recommandés', en: 'Recommended channels' }, sub: { fr: 'Push + SMS + Courriel', en: 'Push + SMS + Email' }, warn: false },
    { key: 'check', title: { fr: 'À vérifier', en: 'Needs verification' }, sub: { fr: 'Heure estimée de rétablissement à confirmer', en: 'Estimated restoration time to be confirmed' }, warn: true },
  ] as const,
  cta: { fr: 'Prêt pour approbation', en: 'Ready for approval' },
}

export const solutionChannels: { key: 'push' | 'sms' | 'email' | 'voice' | 'web' | 'social'; label: Bi }[] = [
  { key: 'push', label: { fr: 'Push', en: 'Push' } },
  { key: 'sms', label: { fr: 'SMS', en: 'SMS' } },
  { key: 'email', label: { fr: 'Courriel', en: 'Email' } },
  { key: 'voice', label: { fr: 'Voix', en: 'Voice' } },
  { key: 'web', label: { fr: 'Web / Appli', en: 'Web / App' } },
  { key: 'social', label: { fr: 'Réseaux sociaux', en: 'Social media' } },
]

export const liveResults = {
  title: { fr: 'Résultats en temps réel', en: 'Real-time results' },
  rate: { fr: '92 %', en: '92%' },
  ratePct: 92,
  reached: { fr: '1 694', en: '1,694' },
  reachedLabel: { fr: 'personnes jointes', en: 'people reached' },
  trend: { fr: '+6 % par rapport à l’envoi précédent', en: '+6% vs previous send' },
  kpis: [
    { key: 'missed', value: { fr: '148', en: '148' }, label: { fr: 'non joints', en: 'not reached' } },
    { key: 'retry', value: { fr: 'Relance', en: 'Follow-up' }, label: { fr: 'en cours', en: 'in progress' } },
    { key: 'calls', value: { fr: '12', en: '12' }, label: { fr: 'appels reçus', en: 'calls received' } },
  ] as const,
  atna: {
    title: { fr: 'ATNA recommande', en: 'ATNA recommends' },
    body: { fr: 'Relance vocale à 18 h 30 pour les 148 non joints.', en: 'Voice follow-up at 6:30 PM for the 148 not reached.' },
    action: { fr: 'Planifier la relance', en: 'Schedule the follow-up' },
  },
}

export const intelligenceSteps: { n: string; title: Bi; body: Bi }[] = [
  { n: '01', title: { fr: 'Comprend', en: 'Understands' }, body: { fr: 'Analyse l’identité et le contexte', en: 'Analyzes identity and context' } },
  { n: '02', title: { fr: 'Prépare', en: 'Prepares' }, body: { fr: 'Rédige et adapte le message', en: 'Drafts and adapts the message' } },
  { n: '03', title: { fr: 'Orchestre', en: 'Orchestrates' }, body: { fr: 'Diffuse sur les bons canaux au bon moment', en: 'Delivers on the right channels at the right time' } },
  { n: '04', title: { fr: 'Vérifie et agit', en: 'Verifies & acts' }, body: { fr: 'Mesure la portée et recommande la suite', en: 'Measures reach and recommends next steps' } },
]

export const solutionClosing = {
  title: { fr: 'D’un événement à une communication prête à diffuser.', en: 'From an event to a communication ready to send.' },
  body: {
    fr: 'Moins de travail manuel. Des messages plus pertinents. Une meilleure portée. Des citoyens mieux informés.',
    en: 'Less manual work. More relevant messages. Better reach. Better-informed residents.',
  },
}
